import { useCallback } from 'react';
import badgeDefinitions from './badgeDefinitions';

export const useBadgeData = (activitySummary, userData, calculateStreaks, formatDate) => {
  return useCallback(() => {
    // Si les données nécessaires ne sont pas disponibles, retourner un résultat par défaut
    if (!activitySummary || !userData || !activitySummary.totalStats || !activitySummary.dailyActivity) {
      return { earned: [], upcoming: [] };
    }

    const earned = [];
    const upcoming = [];
    const flashcardsStudied = activitySummary.totalStats.flashcardsStudied || 0;
    const quizCount = activitySummary.totalStats.quizCount || 0;
    const { current: currentStreak = 0, longest: longestStreak = 0, streak10Count = 0 } = calculateStreaks() || {};
    const timeSpent = activitySummary.totalStats.timeSpent || 0;
    const today = formatDate(new Date());
    const thisWeekStart = new Date(today); 
    thisWeekStart.setDate(thisWeekStart.getDate() - 6);
    const thisMonth = today.slice(0, 7);

    const todayStats = activitySummary.dailyActivity[today] || { quizCount: 0, flashcardsStudied: 0, timeSpent: 0 };
    const dailyFlashcards = todayStats.flashcardsStudied || 0;
    const dailyQuiz = todayStats.quizCount || 0;
    const dailyTime = todayStats.timeSpent || 0;

    // Le reste de la logique reste inchangé...
    const flashcardCategories20 = activitySummary.totalStats.flashcardByCategory 
      ? Object.entries(activitySummary.totalStats.flashcardByCategory).filter(([_, count]) => count >= 20).length 
      : 0;
    const flashcardCategories50 = activitySummary.totalStats.flashcardByCategory 
      ? Object.entries(activitySummary.totalStats.flashcardByCategory).filter(([_, count]) => count >= 50).length 
      : 0;
    const quizCategories3 = activitySummary.totalStats.quizByCategory 
      ? Object.entries(activitySummary.totalStats.quizByCategory).filter(([_, count]) => count >= 5).length 
      : 0;
    const quizCategories10 = activitySummary.totalStats.quizByCategory 
      ? Object.entries(activitySummary.totalStats.quizByCategory).filter(([_, count]) => count >= 10).length 
      : 0;
    const maxFlashcardsSingleCategory = activitySummary.totalStats.flashcardByCategory 
      ? Math.max(...Object.values(activitySummary.totalStats.flashcardByCategory)) 
      : 0;
    const maxQuizSingleCategory = activitySummary.totalStats.quizByCategory 
      ? Math.max(...Object.values(activitySummary.totalStats.quizByCategory)) 
      : 0;

    const boxCounts = { 2: {}, 3: {}, 4: {}, 5: {} };
    if (activitySummary.totalStats.boxProgressionByTheme) {
      Object.entries(activitySummary.totalStats.boxProgressionByTheme).forEach(([category, flashcards]) => {
        Object.entries(flashcards).forEach(([_, box]) => {
          if (box >= 2) {
            boxCounts[box][category] = (boxCounts[box][category] || 0) + 1;
          }
        });
      });
    }
    const box2Count = Object.values(boxCounts[2]).reduce((sum, count) => sum + count, 0);
    const box3Count = Object.values(boxCounts[3]).reduce((sum, count) => sum + count, 0);
    const box4Count = Object.values(boxCounts[4]).reduce((sum, count) => sum + count, 0);
    const box5Count = Object.values(boxCounts[5]).reduce((sum, count) => sum + count, 0);
    const box2Categories = Object.values(boxCounts[2]).filter(count => count >= 5).length;
    const box3Categories = Object.values(boxCounts[3]).filter(count => count >= 10).length;
    const box5SingleCategory = Math.max(...Object.values(boxCounts[5]), 0);
    const balancedBoxes = Object.values({ 
      1: flashcardsStudied - box2Count - box3Count - box4Count - box5Count, 
      2: box2Count, 
      3: box3Count, 
      4: box4Count, 
      5: box5Count 
    }).every(count => count >= 5);

    const weeklyDays = Object.keys(activitySummary.dailyActivity).filter(date => new Date(date) >= thisWeekStart).length;
    const monthlyDays = Object.keys(activitySummary.dailyActivity).filter(date => date.startsWith(thisMonth)).length;
    const yearlyDays = Object.keys(activitySummary.dailyActivity).filter(date => new Date(date) >= new Date(today).setFullYear(new Date(today).getFullYear() - 1)).length;

    badgeDefinitions.forEach(badge => {
      let isEarned = false;
      let current = 0;

      switch (badge.type) {
        case 'flashcards':
          current = flashcardsStudied;
          isEarned = flashcardsStudied >= badge.threshold;
          break;
        case 'dailyFlashcards':
          current = dailyFlashcards;
          isEarned = dailyFlashcards >= badge.threshold;
          break;
        case 'quiz':
          current = quizCount;
          isEarned = quizCount >= badge.threshold;
          break;
        case 'dailyQuiz':
          current = dailyQuiz;
          isEarned = dailyQuiz >= badge.threshold;
          break;
        case 'streak':
          current = currentStreak;
          isEarned = currentStreak >= badge.threshold;
          break;
        case 'longestStreak':
          current = longestStreak;
          isEarned = longestStreak >= badge.threshold;
          break;
        case 'streak10Count':
          current = streak10Count;
          isEarned = streak10Count >= badge.threshold;
          break;
        case 'time':
          current = timeSpent;
          isEarned = timeSpent >= badge.threshold;
          break;
        case 'dailyTime':
          current = dailyTime;
          isEarned = dailyTime >= badge.threshold;
          break;
        case 'weeklyTime':
          current = Object.keys(activitySummary.dailyActivity)
            .filter(date => new Date(date) >= thisWeekStart)
            .reduce((sum, date) => sum + (activitySummary.dailyActivity[date].timeSpent || 0), 0);
          isEarned = current >= badge.threshold;
          break;
        case 'monthlyTime':
          current = Object.keys(activitySummary.dailyActivity)
            .filter(date => date.startsWith(thisMonth))
            .reduce((sum, date) => sum + (activitySummary.dailyActivity[date].timeSpent || 0), 0);
          isEarned = current >= badge.threshold;
          break;
        case 'categories':
          current = flashcardCategories20;
          isEarned = flashcardCategories20 >= badge.threshold;
          break;
        case 'categories50':
          current = flashcardCategories50;
          isEarned = flashcardCategories50 >= badge.threshold;
          break;
        case 'singleCategory':
          current = maxFlashcardsSingleCategory;
          isEarned = maxFlashcardsSingleCategory >= badge.threshold;
          break;
        case 'quizCategories':
          current = quizCategories3;
          isEarned = quizCategories3 >= badge.threshold;
          break;
        case 'quizCategories10':
          current = quizCategories10;
          isEarned = quizCategories10 >= badge.threshold;
          break;
        case 'singleQuizCategory':
          current = maxQuizSingleCategory;
          isEarned = maxQuizSingleCategory >= badge.threshold;
          break;
        case 'box2':
          current = box2Count;
          isEarned = box2Count >= badge.threshold;
          break;
        case 'box3':
          current = box3Count;
          isEarned = box3Count >= badge.threshold;
          break;
        case 'box4':
          current = box4Count;
          isEarned = box4Count >= badge.threshold;
          break;
        case 'box5':
          current = box5Count;
          isEarned = box5Count >= badge.threshold;
          break;
        case 'box2Categories':
          current = box2Categories;
          isEarned = box2Categories >= badge.threshold;
          break;
        case 'box3Categories':
          current = box3Categories;
          isEarned = box3Categories >= badge.threshold;
          break;
        case 'box5SingleCategory':
          current = box5SingleCategory;
          isEarned = box5SingleCategory >= badge.threshold;
          break;
        case 'balancedBoxes':
          current = balancedBoxes ? 25 : 0;
          isEarned = balancedBoxes;
          break;
        case 'weeklyDays':
          current = weeklyDays;
          isEarned = weeklyDays >= badge.threshold;
          break;
        case 'monthlyDays':
          current = monthlyDays;
          isEarned = monthlyDays >= badge.threshold;
          break;
        case 'yearlyDays':
          current = yearlyDays;
          isEarned = yearlyDays >= badge.threshold;
          break;
        case 'dailyDouble':
          current = (dailyQuiz >= 5 && dailyFlashcards >= 20) ? 1 : 0;
          isEarned = current >= badge.threshold;
          break;
        case 'dailyTriple':
          current = (dailyFlashcards >= 10 && dailyQuiz >= 5 && dailyTime >= 60) ? 1 : 0;
          isEarned = current >= badge.threshold;
          break;
        case 'ultimate':
          current = (flashcardsStudied >= 1000 && quizCount >= 100 && currentStreak >= 30) ? 1 : 0;
          isEarned = current >= badge.threshold;
          break;
        default:
          break;
      }

      if (isEarned) {
        earned.push(badge);
      } else {
        upcoming.push({ ...badge, current });
      }
    });

    return { earned, upcoming };
  }, [activitySummary, userData, calculateStreaks, formatDate]);
};